import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingBag,
  FileText,
  HelpCircle,
  Globe,
  FolderKanban,
  Kanban,
  UserCircle,
  Settings,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  ChevronDown,
  Bell,
  Search,
  ArrowLeft,
  Code2,
  Star,
  Inbox,
  Plus,
  CreditCard,
  Receipt,
  ScrollText,
  DollarSign,
  ShieldAlert,
  BookOpen,
  BarChart3,
  Wrench,
  Puzzle,
  LifeBuoy,
  MessageSquare,
  ClipboardList,
  Mail,
  Database,
  Handshake,
  Megaphone,
  Key,
  Phone,
  Activity,
} from 'lucide-react';

const A2G_LOGO = "https://a2gdesigns.com/icons/icon-512x512.png";
import { Button } from '../ui/button';
import NotificationBell from '../notifications/NotificationBell';
import NotificationCenter from '../notifications/NotificationCenter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../ui/dropdown-menu';

// Top menu bar items with dropdowns
const topMenuItems = [
  {
    label: 'Clients',
    items: [
      { label: 'View Clients', path: '/admin/clients', icon: Users },
      { label: 'Add New Client', path: '/admin/clients?action=add', icon: Plus },
    ],
  },
  {
    label: 'Orders',
    items: [
      { label: 'Products & Services', path: '/admin/services', icon: Package },
      { label: 'Products', path: '/admin/products', icon: ShoppingBag },
      { label: 'Domains', path: '/admin/domains', icon: Globe },
    ],
  },
  {
    label: 'Billing',
    items: [
      { label: 'Invoices', path: '/admin/invoices', icon: FileText },
      { label: 'Domain Billing', path: '/admin/domain-billing', icon: Globe },
      { label: 'Transactions', path: '/admin/billing/transactions', icon: DollarSign },
      { label: 'Payment Gateways', path: '/admin/billing/payment-gateways', icon: CreditCard },
      { label: 'Gateway Log', path: '/admin/billing/gateway-log', icon: ScrollText },
    ],
    subMenus: [
      {
        label: 'Invoice Status',
        items: [
          { label: 'Paid', path: '/admin/invoices?status=paid' },
          { label: 'Draft', path: '/admin/invoices?status=draft' },
          { label: 'Unpaid', path: '/admin/invoices?status=unpaid' },
          { label: 'Overdue', path: '/admin/invoices?status=overdue' },
          { label: 'Cancelled', path: '/admin/invoices?status=cancelled' },
          { label: 'Refunded', path: '/admin/invoices?status=refunded' },
          { label: 'Collections', path: '/admin/invoices?status=collections' },
          { label: 'Payment Pending', path: '/admin/invoices?status=payment_pending' },
        ],
      },
    ],
  },
  {
    label: 'Support',
    items: [
      { label: 'Tickets', path: '/admin/support', icon: LifeBuoy },
      { label: 'Inbox', path: '/admin/inbox', icon: MessageSquare },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Dashboard', path: '/admin', icon: BarChart3 },
    ],
  },
  {
    label: 'Utilities',
    perm: 'settings.view',
    items: [
      { label: 'Settings', path: '/admin/settings', icon: Settings, perm: 'settings.view' },
      { label: 'Automation Status', path: '/admin/utilities/automation', icon: ClipboardList, perm: 'automation.view' },
      { label: 'CRM', path: '/admin/crm', icon: UserCircle, perm: 'crm.view' },
    ],
  },
  {
    label: 'Addons',
    items: [
      { label: 'Projects', path: '/admin/projects', icon: FolderKanban },
    ],
  },
];

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', perm: 'dashboard.view' },
  { icon: Inbox, label: 'Inbox', path: '/admin/inbox', perm: 'communications.view' },
  { icon: Users, label: 'Clients', path: '/admin/clients', perm: 'clients.view' },
  { icon: ShoppingBag, label: 'Products', path: '/admin/products', perm: 'products.view' },
  { icon: Package, label: 'Services', path: '/admin/services', perm: 'services.view' },
  { icon: FileText, label: 'Invoices', path: '/admin/invoices', perm: 'invoices.view' },
  { icon: HelpCircle, label: 'Support', path: '/admin/support', perm: 'tickets.view' },
  { icon: Globe, label: 'Domains', path: '/admin/domains', perm: 'domains.view' },
  { icon: Phone, label: 'Phone Numbers', path: '/admin/phone-numbers', perm: 'domains.view' },
  { icon: Activity, label: 'Website Monitor', path: '/admin/website-monitor', perm: 'domains.view' },
  { icon: FolderKanban, label: 'Projects', path: '/admin/projects', perm: 'projects.view' },
  { icon: UserCircle, label: 'CRM', path: '/admin/crm', perm: 'crm.view' },
  { icon: Mail, label: 'Newsletters', path: '/admin/newsletters', perm: 'newsletters.view' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog', perm: 'blog.view' },
  { icon: ClipboardList, label: 'Activity', path: '/admin/activity', perm: 'activity.view' },
  { icon: BarChart3, label: 'Reports', path: '/admin/reports', perm: 'reports.view' },
  { icon: Handshake, label: 'Barter', path: '/admin/barter', perm: 'barter.view' },
  { icon: Mail, label: 'Mail Oversight', path: '/admin/mail-oversight', perm: 'billing.view' },
  { icon: Database, label: 'WHMCS Import', path: '/admin/whmcs-import', perm: 'whmcs.import' },
  { icon: Settings, label: 'Settings', path: '/admin/settings', perm: 'settings.view' },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [commExpanded, setCommExpanded] = useState(false);
  const { user, logout, isLoggedInAsUser, returnToAdmin, originalAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items by permissions
  const userPerms = user?.permissions || [];
  const isFullAdmin = user?.role === 'superuser' || user?.role === 'admin';
  const filteredNavItems = isFullAdmin
    ? navItems
    : navItems.filter(item => !item.perm || userPerms.includes(item.perm));
  
  const isSupport = user?.role === 'support_lvl_1' || user?.role === 'support_lvl_2';
  const isSupportLvl1 = user?.role === 'support_lvl_1';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900 transition-colors duration-300">
      {/* Login As User Banner */}
      {isLoggedInAsUser && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <span className="text-sm font-medium">
            You are logged in as {user?.firstName} {user?.lastName}. Original admin: {originalAdmin?.email}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={returnToAdmin}
            className="bg-white text-amber-600 hover:bg-amber-50"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Return to Admin
          </Button>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 transition-all duration-300 hidden lg:block ${
          sidebarOpen ? 'w-64' : 'w-20'
        } ${isLoggedInAsUser ? 'top-10' : ''}`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-slate-700">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <img 
                src={A2G_LOGO} 
                alt="A2G Designs" 
                className="h-10 w-auto object-contain"
              />
            </div>
          ) : (
            <img 
              src={A2G_LOGO} 
              alt="A2G" 
              className="h-10 w-auto object-contain mx-auto"
            />
          )}
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
          {/* Support Dashboard link for support roles */}
          {isSupport && (
            <Link
              to="/admin/support-dashboard"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                location.pathname === '/admin/support-dashboard'
                  ? 'bg-blue-900 dark:bg-orange-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
              } ${!sidebarOpen ? 'justify-center' : ''}`}
            >
              <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Support Dashboard</span>}
            </Link>
          )}
          {/* Website Monitor — visible to support so they can acknowledge outages */}
          {isSupport && (
            <Link
              to="/admin/website-monitor"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                location.pathname === '/admin/website-monitor'
                  ? 'bg-blue-900 dark:bg-orange-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
              } ${!sidebarOpen ? 'justify-center' : ''}`}
              data-testid="support-website-monitor-link"
            >
              <Activity className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Website Monitor</span>}
            </Link>
          )}
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-900 dark:bg-orange-600 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                } ${!sidebarOpen ? 'justify-center' : ''}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
          
          {/* Communication Accordion */}
          {sidebarOpen ? (
            <div>
              <button
                onClick={() => setCommExpanded(!commExpanded)}
                className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  commExpanded ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400' : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Megaphone className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">Communication</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${commExpanded ? 'rotate-180' : ''}`} />
              </button>
              {commExpanded && (
                <div className="ml-4 mt-1 space-y-1">
                  <Link
                    to="/admin/communications"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      location.pathname === '/admin/communications'
                        ? 'bg-blue-900 dark:bg-orange-600 text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email Center</span>
                  </Link>
                  <button
                    onClick={() => setShowNotificationCenter(true)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-all w-full"
                    data-testid="notif-center-sidebar-btn"
                  >
                    <Bell className="w-4 h-4" />
                    <span>Notification Center</span>
                  </button>
                  <Link
                    to="/admin/newsletters"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                      location.pathname.startsWith('/admin/newsletter')
                        ? 'bg-blue-900 dark:bg-orange-600 text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span>Newsletters</span>
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowNotificationCenter(true)}
              className="flex items-center justify-center px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-all"
              title="Communication"
            >
              <Megaphone className="w-5 h-5" />
            </button>
          )}

          {/* Dev Settings - Superuser Only */}
          {user?.role === 'superuser' && (
            <Link
              to="/dev/forms"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                location.pathname.startsWith('/dev')
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'text-purple-500 hover:bg-purple-100 dark:hover:bg-purple-900/30'
              } ${!sidebarOpen ? 'justify-center' : ''}`}
            >
              <Code2 className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium flex-1">Dev Settings</span>}
            </Link>
          )}
          {user?.role === 'superuser' && (
            <Link
              to="/dev/page-generator-v2"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                location.pathname.startsWith('/dev/page-generator-v2')
                  ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                  : 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
              } ${!sidebarOpen ? 'justify-center' : ''}`}
            >
              <Star className="w-5 h-5 flex-shrink-0 fill-current" />
              {sidebarOpen && (
                <span className="font-medium flex-1">Page Generator V2</span>
              )}
            </Link>
          )}
        </nav>

        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
        >
          <ChevronDown className={`w-4 h-4 text-slate-600 dark:text-slate-400 transition-transform ${sidebarOpen ? 'rotate-90' : '-rotate-90'}`} />
        </button>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed top-0 left-0 w-64 h-full bg-white dark:bg-slate-800 z-50">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <img 
                  src={A2G_LOGO} 
                  alt="A2G Designs" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <button onClick={() => setMobileMenuOpen(false)}>
                <X className="w-6 h-6 text-slate-600 dark:text-slate-400" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-900 dark:bg-orange-600 text-white'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
              
              {/* Dev Settings - Superuser Only */}
              {user?.role === 'superuser' && (
                <Link
                  to="/dev/forms"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    location.pathname.startsWith('/dev')
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'text-purple-500 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                  }`}
                >
                  <Code2 className="w-5 h-5" />
                  <span className="font-medium">Dev Settings</span>
                </Link>
              )}
              {user?.role === 'superuser' && (
                <Link
                  to="/dev/page-generator-v2"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    location.pathname.startsWith('/dev/page-generator-v2')
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                      : 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                  }`}
                >
                  <Star className="w-5 h-5 fill-current" />
                  <span className="font-medium">Page Generator V2</span>
                </Link>
              )}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              <Menu className="w-6 h-6 text-slate-600 dark:text-slate-400" />
            </button>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search clients, invoices, tickets..."
                className="w-80 pl-10 pr-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-lg border-0 text-sm focus:ring-2 focus:ring-blue-900 dark:focus:ring-orange-500 transition-shadow"
              />
            </div>

            {/* WHMCS-style top menu — hidden for support lvl 1 */}
            {!isSupportLvl1 && (
            <nav className="hidden xl:flex items-center gap-0.5" data-testid="admin-top-menu">
              {topMenuItems
                .filter(menu => !menu.perm || isFullAdmin || userPerms.includes(menu.perm))
                .map((menu) => {
                const filteredItems = menu.items.filter(item => !item.perm || isFullAdmin || userPerms.includes(item.perm));
                if (filteredItems.length === 0) return null;
                return (
                <DropdownMenu key={menu.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                      data-testid={`topmenu-${menu.label.toLowerCase()}`}
                    >
                      {menu.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {filteredItems.map((item) => (
                      <DropdownMenuItem
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer"
                        data-testid={`topmenu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.icon && <item.icon className="w-4 h-4 mr-2 text-slate-400" />}
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                    {menu.subMenus?.map((sub) => (
                      <React.Fragment key={sub.label}>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">{sub.label}</div>
                        {sub.items.map((si) => (
                          <DropdownMenuItem
                            key={si.path}
                            onClick={() => navigate(si.path)}
                            className="cursor-pointer pl-6"
                            data-testid={`topmenu-item-${si.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {si.label}
                          </DropdownMenuItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                );
              })}
            </nav>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              {isDark ? <Sun className="w-5 h-5 text-orange-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>

            <NotificationBell isDark={isDark} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                  <div className="w-8 h-8 bg-blue-900 dark:bg-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-600 dark:text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium text-slate-900 dark:text-white">{user?.firstName} {user?.lastName}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{user?.email}</p>
                  {user?.role && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-medium rounded-full">
                      {user.role === 'superuser' ? 'Super Admin' : 'Admin'}
                    </span>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/admin/profile')} className="cursor-pointer">
                  <UserCircle className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                {isFullAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin/settings')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/client')} className="cursor-pointer text-blue-600 dark:text-blue-400">
                  <Users className="w-4 h-4 mr-2" />
                  View Client Portal
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Notification Center Slide-up */}
      {showNotificationCenter && (
        <NotificationCenter onClose={() => setShowNotificationCenter(false)} isDark={isDark} />
      )}
    </div>
  );
};

export default AdminLayout;
